import React, { useState } from "react";
import { Link } from "react-router-dom";

// Client logos
import cartattvaLogo from "../assets/img/clients/Cartattva logo.png";
import splusLogo from "../assets/img/clients/Swasthya logo.png";
import WiseTechLogo from "../assets/img/clients/WiseTech Logo.jpg";
import processControlLogo from "../assets/img/clients/skill logo.jpg";
import IsetLogo from "../assets/img/clients/Iset.png";
import InfinoLogo from "../assets/img/clients/Infino Media.png";

/* =========================================================
   INDUSTRY COLORS
========================================================= */

const accentColors = {
  Automotive: {
    primary: "#2563eb",
    secondary: "#dbeafe",
    soft: "#eff6ff",
  },

  Healthcare: {
    primary: "#0f9f9a",
    secondary: "#ccfbf1",
    soft: "#f0fdfa",
  },

  "Industrial Software": {
    primary: "#0891b2",
    secondary: "#cffafe",
    soft: "#ecfeff",
  },

  "Business Operations": {
    primary: "#4f46e5",
    secondary: "#e0e7ff",
    soft: "#eef2ff",
  },

  Manufacturing: {
    primary: "#7c3aed",
    secondary: "#ede9fe",
    soft: "#f5f3ff",
  },

  "Inventory & Store Management": {
    primary: "#ea580c",
    secondary: "#fed7aa",
    soft: "#fff7ed",
  },

  "Aerospace & Space": {
    primary: "#0891b2",
    secondary: "#cffafe",
    soft: "#ecfeff",
  },
};

/* =========================================================
   CLIENT DETAILS
========================================================= */

const clientDetails = {
  cartattva: {
    logo: cartattvaLogo,
    logoAlt:
      "CarTattva logo - vehicle inspection and automotive software platform",
    website: "https://cartattva.com",
    websiteLabel: "cartattva.com",
  },

  swasthya: {
    logo: splusLogo,
    logoAlt:
      "Swasthya logo - hospital and clinic management software",
    website: "https://swasthyasoftware.in/",
    websiteLabel: "swasthyasoftware.in",
  },

  "inventory-management": {
    logo: InfinoLogo,
    logoAlt:
      "Infino Media logo - inventory and store management software case study",
    website: null,
    websiteLabel: null,
  },

  "process-control": {
    logo: processControlLogo,
    logoAlt:
      "Process Control client logo - industrial manufacturing software",
    website: null,
    websiteLabel: null,
  },

  wisetech: {
    logo: WiseTechLogo,
    logoAlt:
      "WiseTech logo - software license management platform",
    website: "https://wisetech.in/",
    websiteLabel: "wisetech.in",
  },

  satellites: {
    logo: IsetLogo,
    logoAlt:
      "Indo Science Education Trust logo - satellite and aerospace technology project",
    website: "https://indoscience.org/",
    websiteLabel: "indoscience.org",
  },
};

/* =========================================================
   COMPONENT
========================================================= */

export default function CaseStudyCard({
  slug,
  title,
  subtitle,
  industry,
  description,
  highlights = [],
}) {
  const [hovered, setHovered] = useState(false);

  const colors =
    accentColors[industry] || {
      primary: "#2563eb",
      secondary: "#dbeafe",
      soft: "#eff6ff",
    };

  const client = clientDetails[slug];

  const caseStudyUrl = `/case-studies/${slug}`;

  return (
    <article
      itemScope
      itemType="https://schema.org/CreativeWork"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-labelledby={`case-study-${slug}`}
      style={{
        position: "relative",
        height: "100%",
        minHeight: "440px",
        padding: "28px",
        boxSizing: "border-box",
        borderRadius: "24px",
        overflow: "hidden",

        background: hovered
          ? `linear-gradient(145deg, #ffffff 0%, ${colors.soft} 100%)`
          : "#ffffff",

        border: `1px solid ${
          hovered ? colors.secondary : "#e5e7eb"
        }`,

        boxShadow: hovered
          ? "0 24px 60px rgba(15, 23, 42, 0.12)"
          : "0 8px 30px rgba(15, 23, 42, 0.05)",

        transform: hovered
          ? "translateY(-7px)"
          : "translateY(0)",

        transition:
          "transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), " +
          "box-shadow 0.35s ease, " +
          "background 0.35s ease, " +
          "border-color 0.35s ease",

        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* =====================================================
          STRUCTURED DATA
      ====================================================== */}

      <meta
        itemProp="url"
        content={`https://hexxatech.co.in${caseStudyUrl}`}
      />

      <meta
        itemProp="name"
        content={`${title} - ${subtitle}`}
      />

      <meta
        itemProp="description"
        content={description}
      />

      <meta
        itemProp="genre"
        content={industry}
      />

      {/* =====================================================
          BACKGROUND GLOW
      ====================================================== */}

      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          width: "260px",
          height: "260px",
          borderRadius: "50%",
          top: "-150px",
          right: "-100px",

          background: `radial-gradient(
            circle,
            ${colors.secondary} 0%,
            transparent 70%
          )`,

          opacity: hovered ? 0.9 : 0.55,

          transition: "opacity 0.35s ease",

          pointerEvents: "none",
        }}
      />

      {/* =====================================================
          CLIENT HEADER
      ====================================================== */}

      <div
        style={{
          position: "relative",
          zIndex: 2,

          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",

          gap: "15px",

          marginBottom: "26px",
        }}
      >
        {/* Client Logo */}

        <div
          style={{
            width: "150px",
            height: "58px",

            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            background: "#ffffff",

            border: "1px solid #eef1f5",
            borderRadius: "14px",

            padding: "8px 14px",

            boxSizing: "border-box",

            boxShadow: hovered
              ? "0 8px 20px rgba(15,23,42,0.06)"
              : "0 4px 12px rgba(15,23,42,0.035)",

            transition: "all 0.3s ease",

            flexShrink: 0,
          }}
        >
          {client?.logo ? (
            <img
              src={client.logo}
              alt={
                client.logoAlt ||
                `${title} client logo`
              }
              itemProp="image"
              loading="lazy"
              decoding="async"
              width="115"
              height="38"
              style={{
                width: "115px",
                height: "38px",

                objectFit: "contain",
                objectPosition: "center",

                display: "block",
              }}
            />
          ) : (
            <span
              style={{
                fontSize: "14px",
                fontWeight: 700,
                color: "#0f172a",
              }}
            >
              {title}
            </span>
          )}
        </div>

        {/* Industry */}

        <span
          aria-label={`Industry: ${industry}`}
          style={{
            display: "inline-flex",
            alignItems: "center",

            gap: "7px",

            padding: "7px 11px",

            borderRadius: "999px",

            background: colors.soft,
            color: colors.primary,

            fontSize: "10px",
            fontWeight: 700,

            letterSpacing: "0.7px",
            textTransform: "uppercase",

            whiteSpace: "nowrap",
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: "6px",
              height: "6px",

              borderRadius: "50%",

              background: colors.primary,
            }}
          />

          {industry}
        </span>
      </div>

      {/* =====================================================
          CLIENT WEBSITE
      ====================================================== */}

      {client?.website && (
        <div
          style={{
            position: "relative",
            zIndex: 2,

            marginBottom: "18px",
          }}
        >
          <a
            href={client.website}
            target="_blank"
            rel="noopener noreferrer"
            title={`Visit ${title} official website`}
            aria-label={`Visit ${title} official website at ${client.websiteLabel}`}
            style={{
              display: "inline-flex",
              alignItems: "center",

              gap: "6px",

              color: colors.primary,

              textDecoration: "none",

              fontSize: "12px",
              fontWeight: 600,

              transition: "opacity 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "0.7";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "1";
            }}
          >
            {client.websiteLabel}

            <span aria-hidden="true">↗</span>
          </a>
        </div>
      )}

      {/* =====================================================
          CASE STUDY LABEL
      ====================================================== */}

      <div
        style={{
          position: "relative",
          zIndex: 2,

          marginBottom: "8px",

          fontSize: "10px",
          fontWeight: 700,

          color: "#94a3b8",

          letterSpacing: "1.5px",
        }}
      >
        CASE STUDY
      </div>

      {/* =====================================================
          TITLE
      ====================================================== */}

      <h3
        id={`case-study-${slug}`}
        itemProp="headline"
        style={{
          position: "relative",
          zIndex: 2,

          margin: "0 0 8px",

          fontSize: "26px",
          lineHeight: 1.15,

          letterSpacing: "-0.7px",

          fontWeight: 750,

          color: "#0f172a",
        }}
      >
        {title}
      </h3>

      {/* =====================================================
          SUBTITLE
      ====================================================== */}

      <p
        itemProp="alternativeHeadline"
        style={{
          position: "relative",
          zIndex: 2,

          margin: "0 0 16px",

          color: "#475569",

          fontSize: "13px",
          lineHeight: 1.5,

          fontWeight: 600,
        }}
      >
        {subtitle}
      </p>

      {/* =====================================================
          DESCRIPTION
      ====================================================== */}

      <p
        itemProp="text"
        style={{
          position: "relative",
          zIndex: 2,

          margin: 0,

          color: "#64748b",

          fontSize: "13px",
          lineHeight: 1.7,
        }}
      >
        {description}
      </p>

      {/* =====================================================
          DIVIDER
      ====================================================== */}

      <div
        aria-hidden="true"
        style={{
          height: "1px",

          background: hovered
            ? colors.secondary
            : "#e5e7eb",

          margin: "22px 0 18px",

          transition: "background 0.3s ease",
        }}
      />

      {/* =====================================================
          KEY FEATURES
      ====================================================== */}

      {highlights.length > 0 && (
        <div
          aria-label={`${title} key features`}
          style={{
            position: "relative",
            zIndex: 2,

            display: "flex",
            flexWrap: "wrap",

            gap: "7px",
          }}
        >
          {highlights.map((item) => (
            <span
              key={item}
              style={{
                display: "inline-flex",

                padding: "6px 9px",

                borderRadius: "8px",

                background: hovered
                  ? "#ffffff"
                  : "#f8fafc",

                border: "1px solid #e8edf3",

                color: "#475569",

                fontSize: "10.5px",
                lineHeight: 1.2,

                fontWeight: 500,

                transition: "all 0.25s ease",
              }}
            >
              {item}
            </span>
          ))}
        </div>
      )}

      {/* =====================================================
          BOTTOM CTA
      ====================================================== */}

      <div
        style={{
          position: "relative",
          zIndex: 2,

          marginTop: "auto",

          paddingTop: "22px",

          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",

          gap: "12px",
        }}
      >
        {/* =================================================
            INTERNAL CASE STUDY LINK
        ================================================== */}

        <Link
          to={caseStudyUrl}
          aria-label={`Read the ${title} ${industry} case study`}
          title={`Read ${title} case study`}
          style={{
            display: "inline-flex",
            alignItems: "center",

            gap: "8px",

            color: colors.primary,

            textDecoration: "none",

            fontSize: "12px",
            fontWeight: 700,
          }}
        >
          Read {title} case study

          <span
            aria-hidden="true"
            style={{
              fontSize: "16px",

              transform: hovered
                ? "translateX(5px)"
                : "translateX(0)",

              transition: "transform 0.3s ease",
            }}
          >
            →
          </span>
        </Link>

        {/* =================================================
            CLIENT WEBSITE BUTTON
        ================================================== */}

        {client?.website && (
          <a
            href={client.website}
            target="_blank"
            rel="noopener noreferrer"
            title={`Visit ${title} official website`}
            aria-label={`Visit ${title} official website`}
            style={{
              display: "inline-flex",

              alignItems: "center",
              justifyContent: "center",

              width: "34px",
              height: "34px",

              borderRadius: "10px",

              background: hovered
                ? colors.primary
                : colors.soft,

              color: hovered
                ? "#ffffff"
                : colors.primary,

              textDecoration: "none",

              fontSize: "15px",

              transition: "all 0.3s ease",

              flexShrink: 0,
            }}
          >
            <span aria-hidden="true">↗</span>
          </a>
        )}
      </div>
    </article>
  );
}